import { useEffect, useRef, useState } from "react";
import { IconCheck, IconCopy, IconShare } from "@/components/icons";
import { IconButton } from "@/components/admin/IconButton";
import { useClickOutside } from "@/hooks/useClickOutside";
import { getFormPublicIdentifier, getPublicFormUrl } from "@/lib/forms/formUrl";

type FormPublicLinkShareProps = {
  /** CHM-форма — ссылка `/forms/{publicId}` с fallback на числовой id. */
  formId?: number;
  publicId?: string | null;
  /** Внешняя ссылка, например публичная Яндекс-форма. */
  url?: string;
  /** href для перехода по клику на URL в popover (по умолчанию = url или path формы). */
  linkHref?: string;
  popoverTitle?: string;
  size?: "sm" | "md";
  placement?: "top" | "bottom";
  onCopyError?: (message: string) => void;
};

function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//, "");
}

export function FormPublicLinkShare({
  formId,
  publicId,
  url,
  linkHref,
  popoverTitle = "Ссылка на форму",
  size = "sm",
  placement = "bottom",
  onCopyError,
}: FormPublicLinkShareProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const formIdentifier =
    formId != null ? getFormPublicIdentifier({ id: formId, publicId }) : null;
  const publicUrl =
    url ?? (formIdentifier != null ? getPublicFormUrl(formIdentifier) : null);
  const publicPath = linkHref ?? publicUrl;

  useClickOutside(containerRef, () => setOpen(false), open);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  if (!publicUrl || !publicPath) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
    } catch {
      onCopyError?.("Не удалось скопировать ссылку");
    }
  };

  const popoverPositionClass =
    placement === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5";

  return (
    <div ref={containerRef} className="relative">
      <IconButton
        title="Поделиться"
        size={size}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <IconShare size={size === "sm" ? 14 : 15} />
      </IconButton>

      {open ? (
        <div
          className={`absolute right-0 z-30 w-[min(18rem,calc(100vw-2rem))] rounded-lg border border-slate-200/90 bg-white p-2 shadow-lg ${popoverPositionClass}`}
          role="dialog"
          aria-label={popoverTitle}
        >
          <p className="mb-1.5 font-onest text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            {popoverTitle}
          </p>
          <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50/80 p-1">
            <a
              href={publicPath}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 truncate px-1.5 py-1 font-onest text-xs text-brand-700 transition-colors hover:text-brand-600 hover:underline"
              title={publicUrl}
              onClick={() => setOpen(false)}
            >
              {displayUrl(publicUrl)}
            </a>
            <button
              type="button"
              title={copied ? "Ссылка скопирована" : "Копировать ссылку"}
              aria-label={copied ? "Ссылка скопирована" : "Копировать ссылку"}
              onClick={handleCopy}
              className={[
                "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border transition-colors",
                copied
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900",
              ].join(" ")}
            >
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
